/**
 * Orthogonal connector routing for the schematic (D23).
 *
 * How an edge is *drawn*: right-angled segments that leave and enter each port
 * along the way it faces, and go around components rather than through them.
 * Presentation only — derived on every scene build, never stored, never read
 * by anything but the renderer (invariants 4 and 7). It is not Phase 2 routing,
 * which lays out physical channels on manufacturing layers; nothing here knows
 * about layers, widths or design rules, and the path's length means nothing
 * (D18: hydraulic length is a label).
 *
 * The method is the standard one for orthogonal connectors:
 *
 * 1. Inflate every obstacle by a margin, so lines keep clear of symbols.
 * 2. Take the "interesting" coordinates — obstacle edges, the two stub points,
 *    the search area's bounds — and form the grid of their intersections that
 *    lies outside every obstacle. Consecutive grid points on a line are joined
 *    unless the segment between them passes through an obstacle.
 * 3. A* over (point, heading), costing length plus a penalty per bend, so the
 *    route is short and has few corners. The heading at the start is the
 *    source port's direction; arriving against the target port's direction
 *    costs a bend, so lines enter ports head-on.
 *
 * Deterministic: the same inputs give the same path, which the scene memo and
 * any visual regression test rely on.
 */
import type { Direction } from "../design/document";
import type { Bounds, Point } from "./types";

export type Rect = Bounds;

export interface RouteOptions {
  /** Clearance kept between a line and any symbol. */
  margin: number;
  /** How far a line runs straight out of a port before it may turn. */
  stub: number;
  /** Cost of a corner, in scene units of length. */
  bendPenalty: number;
}

export const DEFAULT_ROUTE_OPTIONS: RouteOptions = { margin: 8, stub: 12, bendPenalty: 24 };

export interface RouteEnd {
  point: Point;
  /** Null for an end with no facing, such as one concealed inside a block. */
  direction: Direction | null;
}

const UNIT: Record<Direction, Point> = {
  east: { x: 1, y: 0 },
  south: { x: 0, y: 1 },
  west: { x: -1, y: 0 },
  north: { x: 0, y: -1 },
};

const OPPOSITE: Record<Direction, Direction> = { east: "west", west: "east", north: "south", south: "north" };

const HEADINGS: readonly Direction[] = ["east", "south", "west", "north"];

export function stubOf(end: RouteEnd, length: number): Point {
  if (end.direction === null) return end.point;
  const unit = UNIT[end.direction];
  return { x: end.point.x + unit.x * length, y: end.point.y + unit.y * length };
}

function inflate(rect: Rect, by: number): Rect {
  return { minX: rect.minX - by, minY: rect.minY - by, maxX: rect.maxX + by, maxY: rect.maxY + by };
}

function strictlyInside(rect: Rect, point: Point): boolean {
  return point.x > rect.minX && point.x < rect.maxX && point.y > rect.minY && point.y < rect.maxY;
}

function intersects(a: Rect, b: Rect): boolean {
  return a.minX < b.maxX && a.maxX > b.minX && a.minY < b.maxY && a.maxY > b.minY;
}

/** Drop points that lie on a straight line between their neighbours, and duplicates. */
export function simplify(points: readonly Point[]): Point[] {
  const out: Point[] = [];
  for (const point of points) {
    const last = out[out.length - 1];
    if (last && last.x === point.x && last.y === point.y) continue;
    const before = out[out.length - 2];
    if (before && last && ((before.x === last.x && last.x === point.x) || (before.y === last.y && last.y === point.y))) {
      out[out.length - 1] = point;
      continue;
    }
    out.push(point);
  }
  return out;
}

/**
 * The area a route between two ends may use: their bounding box with room to
 * detour, grown to take in any obstacle it touches so a route can go all the
 * way around one. The scene builder memoises a route on the obstacles in this
 * area, so moving a component re-routes only the edges near it.
 */
export function routeArea(from: RouteEnd, to: RouteEnd, obstacles: readonly Rect[], options = DEFAULT_ROUTE_OPTIONS): Rect {
  const a = stubOf(from, options.stub);
  const b = stubOf(to, options.stub);
  const pad = 4 * options.stub;
  let area: Rect = {
    minX: Math.min(a.x, b.x) - pad,
    minY: Math.min(a.y, b.y) - pad,
    maxX: Math.max(a.x, b.x) + pad,
    maxY: Math.max(a.y, b.y) + pad,
  };
  for (const obstacle of obstacles) {
    const grown = inflate(obstacle, options.margin);
    if (intersects(area, grown)) {
      area = {
        minX: Math.min(area.minX, grown.minX - options.stub),
        minY: Math.min(area.minY, grown.minY - options.stub),
        maxX: Math.max(area.maxX, grown.maxX + options.stub),
        maxY: Math.max(area.maxY, grown.maxY + options.stub),
      };
    }
  }
  return area;
}

/** The obstacles a route in `area` has to respect. */
export function obstaclesIn(area: Rect, obstacles: readonly Rect[], options = DEFAULT_ROUTE_OPTIONS): Rect[] {
  return obstacles.filter((obstacle) => intersects(area, inflate(obstacle, options.margin)));
}

// --- Search -------------------------------------------------------------------

/** A binary min-heap on cost, ties broken by insertion order for determinism. */
class Queue<T> {
  private items: { cost: number; order: number; value: T }[] = [];
  private counter = 0;

  push(cost: number, value: T): void {
    const items = this.items;
    items.push({ cost, order: this.counter++, value });
    let i = items.length - 1;
    while (i > 0) {
      const parent = (i - 1) >> 1;
      if (this.less(items[parent]!, items[i]!)) break;
      [items[parent], items[i]] = [items[i]!, items[parent]!];
      i = parent;
    }
  }

  pop(): T | undefined {
    const items = this.items;
    if (items.length === 0) return undefined;
    const top = items[0]!;
    const last = items.pop()!;
    if (items.length > 0) {
      items[0] = last;
      let i = 0;
      for (;;) {
        const left = 2 * i + 1;
        const right = left + 1;
        let smallest = i;
        if (left < items.length && this.less(items[left]!, items[smallest]!)) smallest = left;
        if (right < items.length && this.less(items[right]!, items[smallest]!)) smallest = right;
        if (smallest === i) break;
        [items[smallest], items[i]] = [items[i]!, items[smallest]!];
        i = smallest;
      }
    }
    return top.value;
  }

  private less(a: { cost: number; order: number }, b: { cost: number; order: number }): boolean {
    return a.cost < b.cost || (a.cost === b.cost && a.order < b.order);
  }
}

function uniqueSorted(values: number[]): number[] {
  return [...new Set(values.map((v) => Math.round(v * 1000) / 1000))].sort((a, b) => a - b);
}

/**
 * A right-angled path between two ends that stays clear of the obstacles,
 * from port to port inclusive. Falls back to a simple dog-leg, ignoring
 * obstacles, when there is no clear route in the area — a line through a
 * symbol is better than no line.
 */
/** How far out of line two ports may be and still be joined by one straight segment. */
export const ALIGN_TOLERANCE = 1;

/** Whether an end faces `toward` along an axis, or has no facing to object. */
function faces(end: RouteEnd, toward: Direction): boolean {
  return end.direction === null || end.direction === toward;
}

/**
 * The axis along which two ports face each other, with the gap between them,
 * or null when they do not face each other.
 */
function facingGap(from: RouteEnd, to: RouteEnd): { axis: "x" | "y"; gap: number; aligned: boolean } | null {
  const dx = to.point.x - from.point.x;
  const dy = to.point.y - from.point.y;
  if (dx > 0 && faces(from, "east") && faces(to, "west")) return { axis: "x", gap: dx, aligned: Math.abs(dy) <= ALIGN_TOLERANCE };
  if (dx < 0 && faces(from, "west") && faces(to, "east")) return { axis: "x", gap: -dx, aligned: Math.abs(dy) <= ALIGN_TOLERANCE };
  if (dy > 0 && faces(from, "south") && faces(to, "north")) return { axis: "y", gap: dy, aligned: Math.abs(dx) <= ALIGN_TOLERANCE };
  if (dy < 0 && faces(from, "north") && faces(to, "south")) return { axis: "y", gap: -dy, aligned: Math.abs(dx) <= ALIGN_TOLERANCE };
  return null;
}

/** Whether the segment a-b runs through the inside of any obstacle. */
function crossesAny(a: Point, b: Point, obstacles: readonly Rect[]): boolean {
  const minX = Math.min(a.x, b.x);
  const maxX = Math.max(a.x, b.x);
  const minY = Math.min(a.y, b.y);
  const maxY = Math.max(a.y, b.y);
  // Touching an edge is not crossing: each end sits on its own symbol's edge.
  return obstacles.some((rect) => minX < rect.maxX && maxX > rect.minX && minY < rect.maxY && maxY > rect.minY);
}

export function routeOrthogonal(
  from: RouteEnd,
  to: RouteEnd,
  obstacles: readonly Rect[],
  given: RouteOptions = DEFAULT_ROUTE_OPTIONS,
  area?: Rect,
): Point[] {
  const facing = facingGap(from, to);
  // Ports facing each other in line, with nothing between them: one straight
  // segment, with no search at all. The search would find the same line when
  // the ports are far apart, but close together its stubs overshoot each
  // other and it draws a loop.
  if (facing?.aligned && !crossesAny(from.point, to.point, obstacles)) {
    return [from.point, to.point];
  }
  // Facing each other but closer than two stubs: shorten the stubs to meet
  // halfway, so the line jogs once between the ports instead of looping back
  // past them.
  const options =
    facing && facing.gap < 2 * given.stub ? { ...given, stub: Math.max(facing.gap / 2, 0) } : given;
  area ??= routeArea(from, to, obstacles, options);
  const start = stubOf(from, options.stub);
  const goal = stubOf(to, options.stub);
  // An obstacle that swallows either stub (overlapping symbols, or the block a
  // concealed end runs into) cannot be avoided, so it is not an obstacle here.
  const blocks = obstacles
    .map((obstacle) => inflate(obstacle, options.margin))
    .filter((rect) => !strictlyInside(rect, start) && !strictlyInside(rect, goal));

  const xs = uniqueSorted([start.x, goal.x, area.minX, area.maxX, ...blocks.flatMap((r) => [r.minX, r.maxX])]).filter(
    (x) => x >= area.minX && x <= area.maxX,
  );
  const ys = uniqueSorted([start.y, goal.y, area.minY, area.maxY, ...blocks.flatMap((r) => [r.minY, r.maxY])]).filter(
    (y) => y >= area.minY && y <= area.maxY,
  );
  const width = xs.length;
  const free = new Uint8Array(xs.length * ys.length);
  for (let j = 0; j < ys.length; j += 1) {
    for (let i = 0; i < width; i += 1) {
      const point = { x: xs[i]!, y: ys[j]! };
      free[j * width + i] = blocks.some((rect) => strictlyInside(rect, point)) ? 0 : 1;
    }
  }
  const index = (point: Point) => {
    const i = xs.indexOf(Math.round(point.x * 1000) / 1000);
    const j = ys.indexOf(Math.round(point.y * 1000) / 1000);
    return i < 0 || j < 0 ? -1 : j * width + i;
  };
  const startIndex = index(start);
  const goalIndex = index(goal);

  /** Whether the segment from a grid point one step in a heading is clear. */
  const step = (cell: number, heading: Direction): number => {
    const i = cell % width;
    const j = (cell - i) / width;
    const ni = i + (heading === "east" ? 1 : heading === "west" ? -1 : 0);
    const nj = j + (heading === "south" ? 1 : heading === "north" ? -1 : 0);
    if (ni < 0 || nj < 0 || ni >= width || nj >= ys.length) return -1;
    const next = nj * width + ni;
    if (!free[next]) return -1;
    const middle = { x: (xs[i]! + xs[ni]!) / 2, y: (ys[j]! + ys[nj]!) / 2 };
    return blocks.some((rect) => strictlyInside(rect, middle)) ? -1 : next;
  };

  const arriving = to.direction ? OPPOSITE[to.direction] : null;
  if (startIndex >= 0 && goalIndex >= 0 && free[startIndex] && free[goalIndex]) {
    // State: cell * 5 + heading index (4 = no heading yet).
    const headingIndex = (heading: Direction | null) => (heading === null ? 4 : HEADINGS.indexOf(heading));
    const best = new Map<number, number>();
    const previous = new Map<number, number>();
    const queue = new Queue<number>();
    const heuristic = (cell: number) => {
      const i = cell % width;
      const j = (cell - i) / width;
      return Math.abs(xs[i]! - goal.x) + Math.abs(ys[j]! - goal.y);
    };
    const first = startIndex * 5 + headingIndex(from.direction);
    best.set(first, 0);
    queue.push(heuristic(startIndex), first);
    let found = -1;
    for (let state = queue.pop(); state !== undefined; state = queue.pop()) {
      const cell = Math.floor(state / 5);
      const headingAt = state % 5;
      const cost = best.get(state)!;
      if (cell === goalIndex) {
        found = state;
        break;
      }
      for (let h = 0; h < 4; h += 1) {
        const heading = HEADINGS[h]!;
        // No doubling back on the line just drawn.
        if (headingAt !== 4 && OPPOSITE[HEADINGS[headingAt]!] === heading) continue;
        const next = step(cell, heading);
        if (next < 0) continue;
        const ci = cell % width;
        const ni = next % width;
        const length = Math.abs(xs[ni]! - xs[ci]!) + Math.abs(ys[(next - ni) / width]! - ys[(cell - ci) / width]!);
        let total = cost + length + (headingAt !== 4 && headingAt !== h ? options.bendPenalty : 0);
        if (next === goalIndex && arriving && heading !== arriving) total += options.bendPenalty;
        const nextState = next * 5 + h;
        if (total < (best.get(nextState) ?? Infinity)) {
          best.set(nextState, total);
          previous.set(nextState, state);
          queue.push(total + heuristic(next), nextState);
        }
      }
    }
    if (found >= 0) {
      const cells: Point[] = [];
      for (let state: number | undefined = found; state !== undefined; state = previous.get(state)) {
        const cell = Math.floor(state / 5);
        const i = cell % width;
        cells.push({ x: xs[i]!, y: ys[(cell - i) / width]! });
      }
      cells.reverse();
      return simplify([from.point, ...cells, to.point]);
    }
  }
  return dogLeg(from, to, start, goal);
}

/**
 * A right-angled path through bends the designer placed (D23).
 *
 * The stored bends are kept exactly; only the joins to each port adapt. Each
 * port still leaves and is entered head-on through its stub, and the elbow
 * from the stub to the first bend arrives across the bend's next segment, so
 * a corner stays a corner when the component at the end is moved.
 */
export function routeThrough(
  from: RouteEnd,
  to: RouteEnd,
  waypoints: readonly Point[],
  options: RouteOptions = DEFAULT_ROUTE_OPTIONS,
): Point[] {
  const start = stubOf(from, options.stub);
  const goal = stubOf(to, options.stub);
  if (waypoints.length === 0) return dogLeg(from, to, start, goal);
  const horizontal = (a: Point, b: Point) => a.y === b.y && a.x !== b.x;
  const sideways = (direction: Direction | null) => direction === "east" || direction === "west";

  const first = waypoints[0]!;
  const second = waypoints[1];
  // Arrive at the first bend crosswise to the segment that leaves it.
  const arriveVertically = second ? horizontal(first, second) : sideways(from.direction);
  const leadIn = arriveVertically ? { x: first.x, y: start.y } : { x: start.x, y: first.y };

  const last = waypoints[waypoints.length - 1]!;
  const beforeLast = waypoints[waypoints.length - 2];
  // Leave the last bend crosswise to the segment that reached it.
  const leaveVertically = beforeLast ? horizontal(beforeLast, last) : !sideways(to.direction);
  const leadOut = leaveVertically ? { x: last.x, y: goal.y } : { x: goal.x, y: last.y };

  return simplify([from.point, start, leadIn, ...waypoints, leadOut, goal, to.point]);
}

/** A simple right-angled connection, for when no clear route exists. */
function dogLeg(from: RouteEnd, to: RouteEnd, start: Point, goal: Point): Point[] {
  const horizontalFirst = from.direction === null || from.direction === "east" || from.direction === "west";
  const middle = horizontalFirst
    ? [{ x: (start.x + goal.x) / 2, y: start.y }, { x: (start.x + goal.x) / 2, y: goal.y }]
    : [{ x: start.x, y: (start.y + goal.y) / 2 }, { x: goal.x, y: (start.y + goal.y) / 2 }];
  return simplify([from.point, start, ...middle, goal, to.point]);
}

/** Where to put a label: the middle of the longest segment, the most legible spot. */
export function labelPoint(path: readonly Point[]): Point {
  let best = { length: -1, point: path[0] ?? { x: 0, y: 0 } };
  for (let i = 1; i < path.length; i += 1) {
    const a = path[i - 1]!;
    const b = path[i]!;
    const length = Math.abs(a.x - b.x) + Math.abs(a.y - b.y);
    if (length > best.length) best = { length, point: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 } };
  }
  return best.point;
}
